import { Component, OnInit, ViewChild } from '@angular/core';
import { ToastComponent } from "../../../../shared/components/toast/toast.component";
import { AdminNavigationComponent } from "../../components/admin-navigation/admin-navigation.component";
import { Router } from '@angular/router';
import { InvoiceService } from '../../../../core/api/invoice.service';
import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import { CommonModule } from '@angular/common';
import { AdminReviewExpandedGasInvoiceComponent } from "../../components/admin-review-expanded-gas-invoice/admin-review-expanded-gas-invoice.component";
import { CustomButtonComponent } from "../../../../shared/components/custom-button/custom-button.component";
import { HttpErrorResponse } from '@angular/common/http';
import { lastValueFrom, combineLatest, Observable } from 'rxjs';
import { LocationService } from '../../../../core/api/location.service';
import { FilesService } from '../../../../core/api/files.service';
import { ApiErrorService } from '../../../../core/api/api-error.service';
import { AdminChangesSendEmail, FileInvoiceReviewGet, InvoiceStatus, ReviewUpdate, VisibilityStatus } from '../../../../core/interfaces/invoice-interfaces';
import { Location } from '../../../../core/interfaces/location';
import { IsoToFormattedDateTimePipe } from "../../../../core/pipes/iso-to-formatted-date-time.pipe";
import { CustomDropdownComponent } from "../../../../shared/components/custom-dropdown/custom-dropdown.component";
import { DateConversionService } from '../../../../core/services/date-conversion.service';
import { FormsModule } from '@angular/forms';
import { AdminTopbarComponent } from "../../components/admin-topbar/admin-topbar.component";
import { DeleteConfirmationDialogComponent } from '../../../../shared/dialogs/delete-confirmation-dialog/delete-confirmation-dialog.component';
import { UtilsService } from '../../../../core/services/utils.service';
import { UserDataService } from '../../../../core/services/user-data.service';
import { AdminInvoiceStatusTextComponent } from '../../components/admin-invoice-status-text/admin-invoice-status-text.component';

@Component({
  selector: 'app-admin-gas-invoice-check-review-page',
  standalone: true,
  imports: [ToastComponent, AdminNavigationComponent, TranslocoPipe, CommonModule, AdminReviewExpandedGasInvoiceComponent, CustomButtonComponent, IsoToFormattedDateTimePipe, CustomDropdownComponent, FormsModule, AdminTopbarComponent, DeleteConfirmationDialogComponent, AdminInvoiceStatusTextComponent],
  templateUrl: './admin-gas-invoice-check-review-page.component.html',
  styleUrl: './admin-gas-invoice-check-review-page.component.css'
})
export class AdminGasInvoiceCheckReviewPageComponent implements OnInit {
  constructor(private router: Router, private invoiceService: InvoiceService, private apiError: ApiErrorService, private filesService: FilesService,
    private locationService: LocationService,
    private translocoService: TranslocoService,
    private dateConversion: DateConversionService,
    private utilsService: UtilsService,
    private userData: UserDataService
  ) { }

  invoices: FileInvoiceReviewGet[] = [];
  loadedInvoices: FileInvoiceReviewGet[] = [];

  lastLoadedPageNumber: number = 0;
  loadMoreHidden = false;

  filterDropdownOptions: string[] = [
    'Filter',
    'By Date (ASC)',
    'By Date (DESC)',
    'By Client (ASC)',
    'By Client (DESC)',
    'By Supplier (ASC)',
    'By Supplier (DESC)',
  ];

  selectedFilterDropdown: number = 0;

  statusDropdownOptions: string[] = [
    'Show For Review',
    'Show Reviewed',
    'Show All'
  ];

  selectedStatusDropdown: number = 0;

  // Search filter
  searchTerm: string = '';

  // Expanded Row Id
  expandedRow: number | null = null;
  loadingExpandedRow: boolean = false;
  private _keepExpandedRow = false;

  expandedFileUrl?: string;
  expandedFileId?: string;

  // Locations for expanded row
  listOfLocations: Location[] = [];

  // Toast
  @ViewChild(ToastComponent) toast!: ToastComponent;

  // Approve invoice
  changingStatus: boolean = false;
  @ViewChild(DeleteConfirmationDialogComponent) confirmStatusUpdate!: DeleteConfirmationDialogComponent;
  confirmDialogTitle = 'admin.finalize_show';
  confirmDialogBody = 'admin.finalize_show_desc';

  ngOnInit(): void {
    const filterKeys = [
      'invoice.filters.default',
      'invoice.filters.date_asc',
      'invoice.filters.date_desc',
      'invoice.filters.client_asc',
      'invoice.filters.client_desc',
      'invoice.filters.supplier_asc',
      'invoice.filters.supplier_desc',
    ];

    const statusSortKeys = [
      'invoice.filters.show_needs_review_only',
      'invoice.filters.show_reviewed_only',
      'invoice.filters.show_all',
    ];

    combineLatest(filterKeys.map((key) => this.translocoService.selectTranslate(key))).subscribe((translations) => {
      this.filterDropdownOptions = translations;
    });

    combineLatest(statusSortKeys.map((key) => this.translocoService.selectTranslate(key))).subscribe((translations) => {
      this.statusDropdownOptions = translations;
    });

    this.loadInvoicesByStatus(0);
  }

  goToDashboard() {
    this.router.navigate(["/dashboard"]);
  }

  async toggleDetails(index: number): Promise<void> {
    if (this.loadingExpandedRow && !this.invoices[index]) {
      return;
    }

    this.loadingExpandedRow = true;

    // Get file url
    this.expandedFileUrl = await this.getFileUrl(this.invoices[index].file.id) ?? undefined;
    this.expandedFileId = this.invoices[index].file.id ?? undefined;

    // Load company locations for dropdown
    await this.reloadLocations(index);

    this.loadingExpandedRow = false;

    this.expandedRow = this.expandedRow === index ? null : index;
  }

  loadInvoicesByStatus(statusIndex: number) {
    this.lastLoadedPageNumber = 0;
    this.loadedInvoices = [];
    this.loadMoreHidden = false;

    if (statusIndex === 0) {
      this.loadGasInvoicesNewPage(() => this.invoiceService.getGasReviewInvoices(this.lastLoadedPageNumber + 1));
    } else if (statusIndex === 1) {
      this.loadGasInvoicesNewPage(() => this.invoiceService.getReviewedOnlyGasReviewInvoices(this.lastLoadedPageNumber + 1));
    } else if (statusIndex === 2) {
      this.loadGasInvoicesNewPage(() => this.invoiceService.getAllGasReviewInvoices(this.lastLoadedPageNumber + 1));
    }
  }

  private loadGasInvoicesNewPage(apiCall: () => Observable<any>) {
    apiCall().subscribe({
      next: (response) => {
        if (response) {
          if (response.data.length === 0 || this.loadedInvoices.length + response.data.length >= response.totalCount) {
            this.loadMoreHidden = true;
          }

          this.loadedInvoices = [...this.loadedInvoices, ...response.data];

          // Applay filter
          this.resetInvoicesFilter();
          this.applySearchFilter();
          this.applySelectedFilter();
          this._keepExpandedRow = false;
        }
      },
      error: async (error) => {
        var crid = 'UNKNOWN-CRID';

        if (error instanceof HttpErrorResponse) {
          crid = error.headers?.get('x-correlation-id') || 'UNKNOWN-CRID';
        }
        this.toast.showToast(await this.apiError.getErrorHeader(), await this.apiError.getErrorMessage(crid), 'error', 8000, false);
      },
      complete: () => {
        this.lastLoadedPageNumber++;
      }
    });
  }

  loadMoreInvoices = () => {
    this.loadGasInvoicesNewPage(this.getCurrentApiCall());
  };

  private getCurrentApiCall() {
    if (this.selectedStatusDropdown === 0) {
      return () => this.invoiceService.getGasReviewInvoices(this.lastLoadedPageNumber + 1);
    } else if (this.selectedStatusDropdown === 1) {
      return () => this.invoiceService.getReviewedOnlyGasReviewInvoices(this.lastLoadedPageNumber + 1);
    } else {
      return () => this.invoiceService.getAllGasReviewInvoices(this.lastLoadedPageNumber + 1);
    }
  }

  applySelectedFilter() {
    if (this.selectedFilterDropdown === undefined) {
      return;
    }
    if (!this._keepExpandedRow) {
      this.expandedRow = null;
    }

    const optionIndex = this.selectedFilterDropdown;

    if (optionIndex === 0) {
      this.resetInvoicesFilter();
      if (this.searchTerm.trim() !== '') {
        this.applySearchFilter();
      }
    }
    else if (optionIndex === 1) {
      this.invoices.sort((a, b) => new Date(a.created_at ?? '').getTime() - new Date(b.created_at ?? '').getTime());
    } else if (optionIndex === 2) {
      this.invoices.sort((a, b) => new Date(b.created_at ?? '').getTime() - new Date(a.created_at ?? '').getTime());
    } else if (optionIndex === 3) {
      this.invoices.sort((a, b) => (a.company?.name ?? '').localeCompare((b.company?.name ?? '')));
    } else if (optionIndex === 4) {
      this.invoices.sort((a, b) => (b.company?.name ?? '').localeCompare((a.company?.name ?? '')));
    } else if (optionIndex === 5) {
      this.invoices.sort((a, b) => (a.supplier_name ?? '').localeCompare(b.supplier_name ?? ''));
    } else if (optionIndex === 6) {
      this.invoices.sort((a, b) => (b.supplier_name ?? '').localeCompare(a.supplier_name ?? ''));
    }
  }

  applySearchFilter() {
    if (!this._keepExpandedRow) {
      this.expandedRow = null;
    }
    this.resetInvoicesFilter();
    const term = this.searchTerm.trim().toLowerCase();

    if (term === '') {
      this.applySelectedFilter();
    }

    this.invoices = this.invoices.filter(invoice => {
      const timestamp = this.dateConversion.isoToFormattedDateTime(invoice.created_at).toLowerCase();
      const client = (invoice.company?.name || '').toLowerCase();
      const supplier = (invoice.supplier_name || '').toLowerCase();

      return timestamp.includes(term) ||
        client.includes(term) ||
        supplier.includes(term);
    });
  }

  resetInvoicesFilter() {
    this.invoices = [];
    this.invoices = this.loadedInvoices;
  }

  applyStatusFilter() {
    this.expandedRow = null;
    this.loadInvoicesByStatus(this.selectedStatusDropdown);
  }

  reloadInvoices = () => {
    this.loadedInvoices = [];
    this.lastLoadedPageNumber = 0;
    this.loadMoreHidden = false;
    this.loadInvoicesByStatus(this.selectedStatusDropdown);
  };

  hardReloadInvoices = () => {
    this.expandedRow = null;
    this.loadingExpandedRow = false;
    this.loadedInvoices = [];
    this.lastLoadedPageNumber = 0;
    this.loadMoreHidden = false;
    this.loadInvoicesByStatus(this.selectedStatusDropdown);
  };

  async getFileUrl(fileId: string) {
    try {
      var response = await lastValueFrom(this.filesService.downloadFile(fileId));
      return response.download_url;

    } catch (error) {
      var crid = 'UNKNOWN-CRID';

      if (error instanceof HttpErrorResponse) {
        crid = error.headers?.get('x-correlation-id') || 'UNKNOWN-CRID';
      }
      this.toast.showToast(await this.apiError.getErrorHeader(), await this.apiError.getErrorMessage(crid), 'error', 8000, false);
    }
    return null;
  }

  reloadCallbackFunction = () => {
    this._keepExpandedRow = true;
    this.reloadInvoices();
  };

  hardReloadCallbackFunction = () => this.hardReloadInvoices();

  createLocationSuccessCallbackFunction = () => this.reloadLocations(this.expandedRow ?? -1);

  async loadLocations(companyId: string) {
    try {
      const response = await lastValueFrom(this.locationService.getLocationsByCompanyId(companyId));
      return response;
    }
    catch (error) {
      var crid = 'UNKNOWN-CRID';

      if (error instanceof HttpErrorResponse) {
        crid = error.headers?.get('x-correlation-id') || 'UNKNOWN-CRID';
      }
      this.toast.showToast(await this.apiError.getErrorHeader(), await this.apiError.getErrorMessage(crid), 'error', 8000, false);
    }
    return null;
  }

  async reloadLocations(index: number) {
    const locations = await this.loadLocations(this.invoices[index].company?.id ?? '');
    if (locations) {
      this.listOfLocations = locations;
    }
  }

  async updateStatus(fileReview: FileInvoiceReviewGet) {
    if (this.changingStatus) { return; }

    this.confirmDialogTitle = 'admin.finalize_show';
    this.confirmDialogBody = 'admin.finalize_show_desc';

    if (fileReview.status === InvoiceStatus.DATA_MISSING) {
      this.toast.showToast('shared.warning', 'admin.cannot_approve_missing_data', 'warning', 5000, true);
      return;
    }

    var dialogResponse = await this.confirmStatusUpdate.openModal();
    if (!dialogResponse) { return; }

    const newStatus: ReviewUpdate = { approve: true };
    this.changingStatus = true;

    try {
      await lastValueFrom(this.invoiceService.updateGasReviewStatusByFile(fileReview.file.id, newStatus));
      this.hardReloadInvoices();
    } catch (error) {
      var crid = 'UNKNOWN-CRID';
      if (error instanceof HttpErrorResponse) {
        crid = error.headers?.get('x-correlation-id') || 'UNKNOWN-CRID';
      }
      this.toast?.showToast(await this.apiError.getErrorHeader(), await this.apiError.getErrorMessage(crid), 'error', 8000, false);
    }

    await this.logFinalizeChanges(fileReview);

    try {
      const adminChangesSendEmail: AdminChangesSendEmail = {
        invoice_number: fileReview.invoice_number ?? undefined
      };
      await lastValueFrom(this.invoiceService.sendFileChangesEmail(fileReview.file.id, adminChangesSendEmail));
    } catch (error) {}

    this.changingStatus = false;
  }

  async logFinalizeChanges(fileReview: FileInvoiceReviewGet) {
    const message = `Admin ${this.userData.getUser()?.first_name} ${this.userData.getUser()?.last_name} (${this.userData.getUser()?.email}) approved this invoice.`;
    const adminChangesLogData = this.utilsService.getAdminChangesLogData(null, message);

    try {
      await lastValueFrom(this.invoiceService.sendFileChangesLog(fileReview.file.id, adminChangesLogData));
    } catch (error) {
    }
  }

  isPendingApproval(fileReview: FileInvoiceReviewGet): boolean {
    return fileReview.status === InvoiceStatus.DONE &&
      fileReview.visibility_status === VisibilityStatus.UNDER_ADMIN_REVIEW;
  }

  isApproved(fileReview: FileInvoiceReviewGet): boolean {
    return fileReview.visibility_status === VisibilityStatus.READY_FOR_USER;
  }

  trackByFileId(index: number, invoice: FileInvoiceReviewGet): string {
    return invoice.file.id;
  }

  isMultiPeriod(fileReview: FileInvoiceReviewGet): boolean {
    return !!fileReview.bimestrale;
  }

  async downloadDisputeReport(fileReview: FileInvoiceReviewGet) {
    try {
      const invoices = await lastValueFrom(this.invoiceService.getGasInvoicesByFile(fileReview.file.id));
      if (invoices && invoices.length > 0) {
        const responseBlob = await lastValueFrom(this.invoiceService.getGasDispute(invoices[0].id ?? ''));
        const blobUrl = URL.createObjectURL(responseBlob);
        window.open(blobUrl, '_blank');
      }
    } catch (error) {
      var crid = 'UNKNOWN-CRID';
      if (error instanceof HttpErrorResponse) {
        crid = error.headers?.get('x-correlation-id') || 'UNKNOWN-CRID';
      }
      this.toast.showToast(await this.apiError.getErrorHeader(), await this.apiError.getErrorMessage(crid), 'error', 8000, false);
    }
  }

  getButtonText(fileReview: FileInvoiceReviewGet): string {
    if (this.isApproved(fileReview)) {
      return this.translocoService.translate('admin.status.approved');
    }
    else if (this.isPendingApproval(fileReview)) {
      return this.translocoService.translate('admin.status.pending_approval');
    }
    return this.translocoService.translate('admin.approve_invoice');
  }

  getButtonBackgroundColor(fileReview: FileInvoiceReviewGet): string {
    return (this.isPendingApproval(fileReview) || this.isApproved(fileReview))
      ? 'bg-success-500'
      : 'bg-purple';
  }

  getButtonBorderColor(fileReview: FileInvoiceReviewGet): string {
    return (this.isPendingApproval(fileReview) || this.isApproved(fileReview))
      ? 'border-success-500'
      : 'border-purple';
  }

  getButtonHoverColor(fileReview: FileInvoiceReviewGet): string {
    return this.isPendingApproval(fileReview)
      ? 'hover:bg-success-600'
      : 'hover:bg-purple-1200';
  }
}
